import React, { useState, useEffect } from "react"; // Import React, useState, useEffect hooks
import Navbar from "../components/Sidebar/Sidebar"; // Import Sidebar component
import axios from "axios"; // Import axios for making HTTP requests
import { useNavigate } from "react-router-dom"; // Import useNavigate for routing
import { toast } from "react-toastify"; // Import toast for displaying notifications
import Loader from "../components/Loader/Loader"; // Import Loader component
import axiosInstance from "../api"; // Import custom axios instance

const DishPage = () => {
  const [calendarData, setCalendarData] = useState({}); // State to store the calendar data
  const navigate = useNavigate(); // Hook for navigation
  const [loading, setLoading] = useState(false); // State to manage loading status

  // Function to fetch data from the server
  const fetchData = async () => {
    try {
      setLoading(true); // Set loading to true when data fetching starts
      const response = await axiosInstance.get(`/api/dish/get-dish`); // Make GET request to fetch dish data
      setLoading(false); // Set loading to false when data is received

      if (response.data.success) { // Check if response is successful
        setCalendarData(response.data.calendarData); // Set the calendar data in state
      }
    } catch (error) {
      setLoading(false); // Set loading to false if there's an error
      console.error("Error fetching dishes:", error); // Log error to console
      toast.error("Failed to load calendar data."); // Show error toast message
    }
  };

  // Function to validate the user's token
  const validateToken = async () => {
    const token = localStorage.getItem("token"); // Retrieve token from local storage

    if (!token) { // If no token found, redirect to login page
      navigate("/login");
      return;
    }

    try {
      // Make a request to validate the token
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/api/auth/validateToken`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`, // Pass token in Authorization header
          },
        }
      );

      if (!response.data.isValid) { // If the token is not valid, redirect to login
        navigate("/login");
      }
    } catch (error) {
      console.error("Token validation error:", error); // Log error to console
      navigate("/login"); // Redirect to login on error
    }
  };

  // useEffect to validate token when the component is mounted
  useEffect(() => {
    validateToken(); // Validate token
  }, []); // Empty dependency array ensures it runs only once on mount

  // useEffect to fetch data when the component is mounted
  useEffect(() => {
    fetchData(); // Fetch data when the component is mounted
  }, []); // Empty dependency array ensures it runs only once on mount

  // Function to handle date click
  const handleDateClick = (date) => {
    navigate(`/dish-description/${date}`); // Navigate to the dish description page for the clicked date
  };

  // Sort the dates in ascending order
  const sortedDates = Object.keys(calendarData).sort(
    (a, b) => new Date(a) - new Date(b) // Compare dates to sort them
  );

  return (
    <div>
      <Navbar /> {/* Render the Navbar component */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Loader /> {/* Display Loader when loading */}
        </div>
      )}
      <div className="p-4 sm:ml-64">
        <h1 className="text-2xl font-bold mb-4 text-center sm:text-left">
          Menu Details {/* Header title for the page */}
        </h1>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-4">
          {sortedDates.map((date) => {
            const isToday =
              new Date(date).toDateString() === new Date().toDateString(); // Check if the date is today
            const isFuture = new Date(date) > new Date(); // Check if the date is in the future

            return (
              <div
                key={date} // Unique key for each date
                className={`p-2 sm:p-4 border rounded-lg cursor-pointer text-xs sm:text-sm ${
                  calendarData[date].hasMenu ? "bg-green-200" : "bg-gray-100"
                }`}
                onClick={() => handleDateClick(date)} // Call handleDateClick when date is clicked
              >
                <p className="text-center font-bold text-black">
                  {new Date(date).getDate()} {/* Display the day of the month */}
                </p>
                {isToday && (
                  <p className="text-center mt-1 sm:mt-2 text-blue-800">
                    📅 Today {/* Show "Today" if the date is today */}
                  </p>
                )}
                {isFuture && (
                  <p className="text-center mt-1 sm:mt-2 text-yellow-800">
                    ⏳ Upcoming {/* Show "Upcoming" if the date is in the future */}
                  </p>
                )}
                {calendarData[date].hasMenu ? (
                  <p className="text-center mt-1 sm:mt-2 text-green-800">
                    🍴 Menu Available {/* Show "Menu Available" if there is a menu */}
                  </p>
                ) : (
                  <p className="text-center mt-1 sm:mt-2 text-gray-600">
                    No Menu {/* Show "No Menu" if no menu is available */}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DishPage; // Export the DishPage component
